import { DynamoDBClient, GetItemCommand, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { MeetingNotification } from '../interfaces/interface';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';
import { logger } from 'src/logger';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const TABLE_NAME = `finsuite_ea_huddle_employee_settings_${normalizedStage}`;

/** Global cap across all meetings to stay within DDB 400KB item limit. */
const MAX_NOTIFICATIONS = 200;

/**
 * Service for managing user notifications in DynamoDB.
 *
 * Schema:
 * - pk: employee_alias, sk: NOTIFICATIONS
 *   { meetingNotifications: { [meetingId]: MeetingNotification[] } }
 *
 * When the total notification count reaches MAX_NOTIFICATIONS, the oldest
 * notification across all meetings is evicted to make room.
 */
export class NotificationService {
  private static client: DynamoDBClient | null = null;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  /** Get all notifications across all meetings for a recipient. */
  public static async getNotifications(recipientAlias: string): Promise<Record<string, MeetingNotification[]>> {
    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({ employee_alias: recipientAlias, sk: 'NOTIFICATIONS' })
      })
    );
    if (!result.Item) return {};
    const item = unmarshall(result.Item);
    return (item.meetingNotifications as Record<string, MeetingNotification[]>) ?? {};
  }

  /**
   * Add a notification for a recipient. If at cap, evicts the oldest notification.
   */
  public static async addNotification(recipientAlias: string, notification: MeetingNotification): Promise<void> {
    const all = await this.getNotifications(recipientAlias);
    const { meetingId } = notification;

    // Append to meeting list
    if (!all[meetingId]) all[meetingId] = [];
    all[meetingId].push(notification);

    // Evict oldest if over cap
    const totalCount = Object.values(all).reduce((sum, list) => sum + list.length, 0);
    if (totalCount > MAX_NOTIFICATIONS) {
      this.evictOldest(all, totalCount - MAX_NOTIFICATIONS);
    }

    await this.writeNotifications(recipientAlias, all);
  }

  /** Mark a single notification as read. */
  public static async markAsRead(recipientAlias: string, meetingId: string, notificationId: string): Promise<void> {
    const all = await this.getNotifications(recipientAlias);
    const meetingNotifs = all[meetingId];
    if (!meetingNotifs) return;

    const target = meetingNotifs.find((n) => n.id === notificationId);
    if (!target || target.isRead) return;

    target.isRead = true;
    await this.writeNotifications(recipientAlias, all);
  }

  /** Mark all notifications as read for a recipient. */
  public static async markAllAsRead(recipientAlias: string): Promise<void> {
    const all = await this.getNotifications(recipientAlias);

    for (const notifs of Object.values(all)) {
      for (const n of notifs) {
        n.isRead = true;
      }
    }

    await this.writeNotifications(recipientAlias, all);
  }

  /** Dismiss (delete) a single notification. */
  public static async dismissNotification(recipientAlias: string, meetingId: string, notificationId: string): Promise<void> {
    const all = await this.getNotifications(recipientAlias);
    const meetingNotifs = all[meetingId];
    if (!meetingNotifs) return;

    const filtered = meetingNotifs.filter((n) => n.id !== notificationId);
    if (filtered.length === 0) {
      delete all[meetingId];
    } else {
      all[meetingId] = filtered;
    }

    await this.writeNotifications(recipientAlias, all);
  }

  // ── Private helpers ──────────────────────────────────────────────────

  /** Write the full notifications map back to DDB. */
  private static async writeNotifications(recipientAlias: string, all: Record<string, MeetingNotification[]>): Promise<void> {
    const client = this.getClient();
    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({ employee_alias: recipientAlias, sk: 'NOTIFICATIONS' }),
        UpdateExpression: 'SET meetingNotifications = :map, updatedAt = :now',
        ExpressionAttributeValues: marshall({ ':map': all, ':now': new Date().toISOString() }, { removeUndefinedValues: true })
      })
    );
  }

  /** Evict the N oldest notifications across all meetings. */
  private static evictOldest(all: Record<string, MeetingNotification[]>, count: number): void {
    // Collect all notifications with their meeting key
    const indexed: Array<{ meetingId: string; idx: number; createdAt: string }> = [];
    for (const [mid, notifs] of Object.entries(all)) {
      notifs.forEach((n, i) => indexed.push({ meetingId: mid, idx: i, createdAt: n.createdAt }));
    }

    // Sort oldest first
    indexed.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    // Mark oldest N for removal (process in reverse index order to avoid shifting)
    const toRemove = indexed.slice(0, count);
    const grouped = new Map<string, number[]>();
    for (const item of toRemove) {
      if (!grouped.has(item.meetingId)) grouped.set(item.meetingId, []);
      grouped.get(item.meetingId)!.push(item.idx);
    }

    for (const [mid, indices] of grouped.entries()) {
      // Remove in reverse order so indices stay valid
      for (const idx of indices.sort((a, b) => b - a)) {
        all[mid].splice(idx, 1);
      }
      if (all[mid].length === 0) delete all[mid];
    }
  }
}
